import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Channel } from '@/lib/channels';
import { Logo } from '@/components/common/Logo';
import { FlagIcon } from '@/components/common/FlagIcon';
import { Play } from 'lucide-react';

interface ChannelListProps {
  channels: Channel[];
  onChannelClick: (channel: Channel) => void;
  activeChannelId?: string;
}

export function ChannelList({ channels, onChannelClick, activeChannelId }: ChannelListProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: channels.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 64,
    overscan: 10,
  });

  return (
    <div ref={parentRef} className="h-full overflow-auto">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const channel = channels[virtualRow.index];
          const isActive = channel.id === activeChannelId;
          return (
            <button
              key={virtualRow.key}
              onClick={() => onChannelClick(channel)}
              className={`absolute top-0 left-0 w-full flex items-center gap-3 px-3 py-3 border-b border-border hover:bg-bg-hover transition-colors text-left ${
                isActive ? 'bg-accent/10' : ''
              }`}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                height: `${virtualRow.size}px`,
              }}
            >
              <div className="h-10 w-10 flex-shrink-0 rounded overflow-hidden">
                <Logo name={channel.name} url={channel.logo} className="w-full h-full" size="sm" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate" title={channel.name}>
                  {channel.name}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-text-muted">
                  <FlagIcon code={channel.countryCode} size="sm" />
                  <span className="truncate">{channel.country}</span>
                </div>
              </div>
              {isActive && (
                <span className="text-xs font-bold text-accent uppercase tracking-wider flex-shrink-0">
                  Playing
                </span>
              )}
              {!isActive && (
                <Play className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}